import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  Lock, 
  Eye, 
  FileText, 
  UserCheck, 
  Trash2, 
  Download, 
  Mail,
  Building,
  Clock,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/services/api';

export default function PolitiqueConfidentialite() {
  const [settings, setSettings] = useState({
    company_name: 'Jeld-Wen France SAS',
    company_address: 'Zone Industrielle, 32800 Eauze',
    dpo_email: 'dpo@jeldwen.com',
    dpo_name: '',
    privacy_policy_version: '2.0',
    privacy_policy_last_update: '2025-12-30'
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api.getPublicSettings();
      if (data) {
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.log('Using default settings');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Politique de Confidentialité</h1>
          <p className="text-gray-500 mt-2">Charte RGPD - EHS Training Management</p>
          <p className="text-sm text-gray-400 mt-1">
            Version {settings.privacy_policy_version} - Dernière mise à jour : {formatDate(settings.privacy_policy_last_update)}
          </p>
        </div>

        {/* Introduction */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5 text-blue-600" />
              1. Responsable du traitement
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>
              La société <strong>{settings.company_name}</strong>, située à {settings.company_address}, est responsable 
              du traitement des données personnelles collectées via l'application EHS Training Management.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="font-medium mb-2">Contact du Délégué à la Protection des Données (DPO) :</p>
              <ul className="list-none space-y-1">
                {settings.dpo_name && <li>👤 Nom : {settings.dpo_name}</li>}
                <li>📧 Email : <a href={`mailto:${settings.dpo_email}`} className="text-blue-600 hover:underline">{settings.dpo_email}</a></li>
                <li>📍 Adresse : {settings.company_name}, {settings.company_address}</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Données collectées */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              2. Données personnelles collectées
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>Dans le cadre de la gestion des autorisations de conduite, des formations HSE et des compétences, nous collectons :</p>
            
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">👤 Données d'identité</h4>
                <ul className="text-sm space-y-1 text-blue-800">
                  <li>• Nom et prénom</li>
                  <li>• Matricule employé</li>
                  <li>• Adresse email professionnelle</li>
                  <li>• Numéro de téléphone professionnel</li>
                  <li>• Photo d'identité (si fournie)</li>
                  <li>• QR code d'identification unique</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-2">💼 Données professionnelles</h4>
                <ul className="text-sm space-y-1 text-green-800">
                  <li>• Poste occupé</li>
                  <li>• Secteur / Service</li>
                  <li>• Date d'embauche</li>
                  <li>• Manager hiérarchique</li>
                </ul>
              </div>
              
              <div className="bg-amber-50 p-4 rounded-lg">
                <h4 className="font-semibold text-amber-900 mb-2">🎓 Données de formation</h4>
                <ul className="text-sm space-y-1 text-amber-800">
                  <li>• Certificats CACES obtenus</li>
                  <li>• Formations suivies</li>
                  <li>• Dates de validité</li>
                  <li>• Organismes de formation</li>
                </ul>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-2">🎯 Données de compétences</h4>
                <ul className="text-sm space-y-1 text-purple-800">
                  <li>• Évaluations de compétences (niveau 1 à 4)</li>
                  <li>• Historique des évaluations</li>
                  <li>• Commentaires d'évaluation</li>
                  <li>• Date et évaluateur</li>
                  <li>• Habilitations machines</li>
                </ul>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-semibold text-red-900 mb-2">🏥 Données de santé (sensibles)</h4>
                <ul className="text-sm space-y-1 text-red-800">
                  <li>• Aptitude médicale (apte/inapte)</li>
                  <li>• Restrictions éventuelles</li>
                  <li>• Dates des visites médicales</li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">Données sensibles</p>
                  <p className="text-sm text-yellow-700">
                    Les données de santé sont traitées conformément à l'article 9 du RGPD, 
                    sur la base de l'obligation légale de l'employeur en matière de sécurité au travail.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Finalités */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              3. Finalités du traitement
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>Vos données personnelles sont traitées pour les finalités suivantes :</p>
            
            <div className="space-y-3 mt-4">
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium">Gestion des autorisations de conduite</p>
                  <p className="text-sm text-gray-600">
                    Délivrance, suivi et renouvellement des autorisations de conduite d'engins 
                    conformément au Code du travail (articles R.4323-55 à R.4323-57).
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium">Suivi des formations obligatoires</p>
                  <p className="text-sm text-gray-600">
                    Planification et traçabilité des formations HSE réglementaires et internes.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium">Gestion et évaluation des compétences</p>
                  <p className="text-sm text-gray-600">
                    Évaluation des compétences professionnelles des collaborateurs sur une échelle de 1 à 4 
                    (Débutant, Intermédiaire, Confirmé, Expert) pour garantir l'adéquation poste/compétences.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium">Vérification des habilitations machines</p>
                  <p className="text-sm text-gray-600">
                    Contrôle via QR code que le collaborateur possède les compétences minimales requises 
                    pour utiliser une machine spécifique, garantissant la sécurité au poste de travail.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium">Vérification de l'aptitude médicale</p>
                  <p className="text-sm text-gray-600">
                    S'assurer que les collaborateurs disposent d'une aptitude médicale valide 
                    pour la conduite d'engins.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium">Génération de documents officiels</p>
                  <p className="text-sm text-gray-600">
                    Création des autorisations de conduite avec signature électronique et QR code de vérification.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Base légale */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-600" />
              4. Base légale du traitement
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>Le traitement de vos données repose sur les bases légales suivantes :</p>
            
            <table className="w-full mt-4 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left">Type de données</th>
                  <th className="p-3 text-left">Base légale (Art. 6 RGPD)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3">Données d'identité et professionnelles</td>
                  <td className="p-3">Exécution du contrat de travail (Art. 6.1.b)</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Données de formation CACES</td>
                  <td className="p-3">Obligation légale (Art. 6.1.c) - Code du travail</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Évaluations de compétences</td>
                  <td className="p-3">Intérêt légitime (Art. 6.1.f) - Sécurité au travail et adéquation poste/compétences</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Habilitations machines</td>
                  <td className="p-3">Obligation légale (Art. 6.1.c) - Prévention des risques professionnels</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Données de santé (aptitude)</td>
                  <td className="p-3">Obligation légale (Art. 6.1.c + Art. 9.2.b) - Médecine du travail</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">QR code d'identification</td>
                  <td className="p-3">Intérêt légitime (Art. 6.1.f) - Vérification rapide des habilitations</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Signature électronique</td>
                  <td className="p-3">Intérêt légitime (Art. 6.1.f) - Authenticité des documents</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Durée de conservation */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              5. Durée de conservation
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>Les données sont conservées pendant les durées suivantes :</p>
            
            <table className="w-full mt-4 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left">Type de données</th>
                  <th className="p-3 text-left">Durée de conservation</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3">Données du collaborateur actif</td>
                  <td className="p-3">Durée du contrat de travail</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Autorisations de conduite</td>
                  <td className="p-3">5 ans après expiration</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Certificats CACES</td>
                  <td className="p-3">5 ans après expiration</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Attestations de formation</td>
                  <td className="p-3">Durée du contrat + 5 ans</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Évaluations de compétences</td>
                  <td className="p-3">Durée du contrat + 3 ans (historique)</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Habilitations machines</td>
                  <td className="p-3">Durée du contrat + 5 ans</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Données de santé</td>
                  <td className="p-3">Durée du contrat + 10 ans (prescription)</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Logs de connexion et scans QR</td>
                  <td className="p-3">1 an</td>
                </tr>
              </tbody>
            </table>

            <p className="text-sm text-gray-500 mt-4">
              À l'issue de ces durées, les données sont supprimées ou anonymisées de manière irréversible.
            </p>
          </CardContent>
        </Card>

        {/* Destinataires */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-blue-600" />
              6. Destinataires des données
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>Vos données sont accessibles <strong>uniquement en interne</strong> aux personnes habilitées :</p>
            
            <div className="space-y-3 mt-4">
              <div className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                <p className="font-medium text-blue-900">🏢 Services internes uniquement</p>
                <ul className="text-sm text-blue-800 mt-2 space-y-1">
                  <li>• <strong>Service HSE</strong> (Hygiène Sécurité Environnement) - Gestion complète</li>
                  <li>• <strong>Direction du site</strong> - Validation et signature des autorisations</li>
                  <li>• <strong>Service Ressources Humaines</strong> - Gestion des collaborateurs et formations</li>
                  <li>• <strong>Managers</strong> - Consultation pour leur équipe uniquement</li>
                  <li>• <strong>Service Maintenance</strong> - Consultation des autorisations de conduite</li>
                </ul>
              </div>
              
              <div className="p-3 border rounded-lg">
                <p className="font-medium">⚖️ Autorités compétentes (sur demande légale uniquement)</p>
                <ul className="text-sm text-gray-600 mt-2 space-y-1">
                  <li>• Inspection du travail</li>
                  <li>• CARSAT / CRAMIF</li>
                  <li>• Autorités judiciaires</li>
                </ul>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 p-4 rounded-lg mt-4">
              <p className="text-sm text-green-800">
                <strong>✓ Aucun sous-traitant externe</strong> n'a accès à vos données personnelles.<br/>
                <strong>✓ Aucun transfert hors UE</strong> n'est effectué.<br/>
                <strong>✓ Toutes les données sont hébergées et traitées en interne.</strong>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Droits */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              7. Vos droits
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>Conformément au RGPD, vous disposez des droits suivants :</p>
            
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">Droit d'accès</span>
                </div>
                <p className="text-sm text-gray-600">
                  Obtenir une copie de toutes vos données personnelles détenues.
                </p>
              </div>
              
              <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  <span className="font-medium">Droit de rectification</span>
                </div>
                <p className="text-sm text-gray-600">
                  Corriger des données inexactes ou incomplètes.
                </p>
              </div>
              
              <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-2">
                  <Trash2 className="w-5 h-5 text-red-600" />
                  <span className="font-medium">Droit à l'effacement</span>
                </div>
                <p className="text-sm text-gray-600">
                  Demander la suppression de vos données (sous réserve des obligations légales).
                </p>
              </div>
              
              <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-2">
                  <Download className="w-5 h-5 text-purple-600" />
                  <span className="font-medium">Droit à la portabilité</span>
                </div>
                <p className="text-sm text-gray-600">
                  Recevoir vos données dans un format structuré et lisible.
                </p>
              </div>
              
              <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-5 h-5 text-amber-600" />
                  <span className="font-medium">Droit à la limitation</span>
                </div>
                <p className="text-sm text-gray-600">
                  Limiter temporairement le traitement de vos données.
                </p>
              </div>
              
              <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  <span className="font-medium">Droit d'opposition</span>
                </div>
                <p className="text-sm text-gray-600">
                  S'opposer au traitement pour motifs légitimes.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg mt-6">
              <p className="font-medium text-blue-900 mb-2">📧 Pour exercer vos droits :</p>
              <p className="text-sm text-blue-800">
                Contactez notre DPO par email à <a href={`mailto:${settings.dpo_email}`} className="font-semibold text-blue-600 hover:underline">{settings.dpo_email}</a> ou 
                utilisez la fonction "Mes données personnelles" dans votre profil.
              </p>
              <p className="text-sm text-blue-800 mt-2">
                Vous pouvez également introduire une réclamation auprès de la CNIL : 
                <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="underline ml-1">
                  www.cnil.fr
                </a>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Sécurité */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-600" />
              8. Sécurité des données
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>Nous mettons en œuvre les mesures techniques et organisationnelles suivantes :</p>
            
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <h4 className="font-medium">🔐 Mesures techniques</h4>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>✓ Chiffrement des données en transit (HTTPS/TLS)</li>
                  <li>✓ Chiffrement des mots de passe (bcrypt)</li>
                  <li>✓ Authentification par JWT sécurisé</li>
                  <li>✓ Protection contre les attaques brute-force</li>
                  <li>✓ Journalisation des accès</li>
                  <li>✓ Sauvegardes régulières chiffrées</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-medium">👥 Mesures organisationnelles</h4>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>✓ Accès limité au besoin d'en connaître</li>
                  <li>✓ Gestion des rôles et permissions</li>
                  <li>✓ Sensibilisation des utilisateurs</li>
                  <li>✓ Procédure de gestion des incidents</li>
                  <li>✓ Revue périodique des accès</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* QR Code et vérification */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              9. QR Code et vérification des compétences
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>L'application génère un QR code unique pour chaque collaborateur permettant :</p>
            
            <div className="space-y-3 mt-4">
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <p className="font-medium text-purple-900">Vérification instantanée des habilitations</p>
                  <p className="text-sm text-purple-800">
                    En scannant le QR code, un utilisateur autorisé peut vérifier si le collaborateur 
                    possède les compétences requises pour utiliser une machine spécifique.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                <Lock className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <p className="font-medium text-purple-900">Accès sécurisé</p>
                  <p className="text-sm text-purple-800">
                    Seuls les utilisateurs authentifiés peuvent accéder aux informations de vérification. 
                    Le QR code ne contient que l'identifiant du collaborateur, pas ses données personnelles.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-4">
              <p className="font-medium text-blue-900 mb-2">📱 Données accessibles via QR code</p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Nom et prénom du collaborateur</li>
                <li>• Matricule</li>
                <li>• Liste des machines autorisées</li>
                <li>• Statut des compétences (Autorisé / Non autorisé)</li>
              </ul>
              <p className="text-sm text-blue-700 mt-2 italic">
                Aucune donnée sensible (santé, évaluations détaillées) n'est accessible via le scan du QR code.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Cookies */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              10. Cookies et traceurs
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>L'application utilise uniquement des cookies strictement nécessaires :</p>
            
            <table className="w-full mt-4 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left">Cookie</th>
                  <th className="p-3 text-left">Finalité</th>
                  <th className="p-3 text-left">Durée</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3 font-mono text-xs">access_token</td>
                  <td className="p-3">Authentification de session</td>
                  <td className="p-3">1 heure</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-mono text-xs">refresh_token</td>
                  <td className="p-3">Renouvellement de session</td>
                  <td className="p-3">7 jours</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-mono text-xs">rgpd_consent</td>
                  <td className="p-3">Mémorisation du consentement</td>
                  <td className="p-3">1 an</td>
                </tr>
              </tbody>
            </table>
            
            <p className="text-sm text-gray-500 mt-4">
              <strong>Aucun cookie tiers</strong> (analytics, publicité, réseaux sociaux) n'est utilisé.
            </p>
          </CardContent>
        </Card>

        {/* Modifications */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              11. Modifications de la politique
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <p>
              Cette politique de confidentialité peut être mise à jour pour refléter les évolutions 
              légales ou les changements dans nos pratiques. En cas de modification substantielle, 
              vous serez informé par email ou notification dans l'application.
            </p>
            <p className="mt-2">
              La date de dernière mise à jour est indiquée en haut de ce document.
            </p>
            
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="font-medium mb-2">📋 Historique des versions</p>
              <ul className="text-sm space-y-1 text-gray-600">
                <li>• <strong>Version 2.0</strong> (30/12/2025) : Ajout gestion des compétences, QR codes, habilitations machines</li>
                <li>• <strong>Version 1.0</strong> (01/01/2025) : Version initiale</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <Link to="/mon-profil">
            <Button variant="outline" className="w-full sm:w-auto">
              <Download className="w-4 h-4 mr-2" />
              Télécharger mes données
            </Button>
          </Link>
          <Link to="/">
            <Button className="w-full sm:w-auto">
              Retour à l'application
            </Button>
          </Link>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>© {new Date().getFullYear()} {settings.company_name} - Tous droits réservés</p>
          <p className="mt-1">
            Application EHS Training Management - Version 2.0
          </p>
        </div>
      </div>
    </div>
  );
}
